import React from "react";
import DatePicker from "react-datepicker";
import { styled } from "styled-components";
import Box from "../box";
import { Colors } from "@blueprintjs/core";

export type CalendarModeT =
  | "in_year"
  | "in_quarter"
  | "between_year"
  | "between_quarter"
  | "between_date"
  | "in_date";

interface CustomDatePickerProps {
  value?: Date | string | null;
  showIcon?: boolean;
  inline?: boolean;
  onValueChange: (dates: (Date | null)[]) => void;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  mode?: CalendarModeT;
  renderQuarterContent?: (quarter: any, shortQuarter: any) => React.ReactNode;
  renderYearContent?: (year: any) => React.ReactNode;
  className?: string;
  wrapperClassName?: string;
  yearItemNumber?: number;
}
function yearToDate(
  val: Date | string | number | null | undefined
): Date | null {
  if (!val) return null;
  if (val instanceof Date) {
    if (isNaN(val.getTime())) return null;
    return new Date(val.getFullYear(), 0, 1);
  }
  if (typeof val === "string" && /^\d{4}$/.test(val)) {
    const d = new Date(Number(val), 0, 1);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val === "number") {
    const d = new Date(val, 0, 1);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function quarterStringToDate(
  val: Date | string | null | undefined
): Date | null {
  if (!val) return null;
  if (val instanceof Date) {
    if (isNaN(val.getTime())) return null;
    return val;
  }
  const match = typeof val === "string" && val.match(/^(\d{4})-Q([1-4])$/);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const quarter = parseInt(match[2], 10);
  const month = (quarter - 1) * 3;
  const d = new Date(year, month, 1);
  return isNaN(d.getTime()) ? null : d;
}

function safeToDate(val: Date | string | null | undefined): Date | null {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

function normalizeDate(
  val: Date | string | number | null | undefined,
  mode: CalendarModeT
): Date | null {
  if (val === undefined || val === "") return null;
  if (mode === "in_year" || mode === "between_year") return yearToDate(val);
  if (mode === "in_quarter" || mode === "between_quarter")
    return quarterStringToDate(val as Date | string);
  return safeToDate(val as Date);
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  showIcon = true,
  inline = false,
  onValueChange,
  startDate,
  endDate,
  mode = "in_date",
  renderQuarterContent,
  renderYearContent,
  className = "w-100",
  wrapperClassName = "w-100",
  yearItemNumber = 10,
}) => {
  const normalizedValue = normalizeDate(value, mode);
  const normalizedStart = normalizeDate(startDate, mode);
  const normalizedEnd = normalizeDate(endDate, mode);
  const today = new Date();

  if (mode === "between_quarter") {
    return (
      <DatePickerWrapper flexDir="row" gap={"small"}>
        <DatePicker
          className={className}
          wrapperClassName={wrapperClassName}
          renderQuarterContent={renderQuarterContent}
          showQuarterYearPicker
          dateFormat="QQQ, yyyy"
          showIcon={showIcon}
          inline={inline}
          toggleCalendarOnIconClick
          selected={normalizedStart}
          onChange={(date) => onValueChange([date, normalizedEnd])}
          placeholderText="Start Quarter"
          maxDate={
            normalizedEnd && normalizedEnd < today ? normalizedEnd : today
          }
          enableTabLoop={false}
        />
        <DatePicker
          className={className}
          wrapperClassName={wrapperClassName}
          renderQuarterContent={renderQuarterContent}
          showQuarterYearPicker
          dateFormat="QQQ, yyyy"
          showIcon={showIcon}
          inline={inline}
          toggleCalendarOnIconClick
          selected={normalizedEnd}
          onChange={(date) => onValueChange([normalizedStart, date])}
          placeholderText="End Quarter"
          maxDate={today}
          minDate={normalizedStart || undefined}
          enableTabLoop={false}
        />
      </DatePickerWrapper>
    );
  }
  if (mode === "between_year") {
    return (
      <DatePickerWrapper flexDir="row" gap={"small"}>
        <DatePicker
          className={className}
          wrapperClassName={wrapperClassName}
          renderYearContent={renderYearContent}
          showYearPicker
          dateFormat="yyyy"
          showIcon={showIcon}
          inline={inline}
          toggleCalendarOnIconClick
          selected={normalizedStart}
          onChange={(date) => onValueChange([date, normalizedEnd])}
          placeholderText="Start Year"
          maxDate={
            normalizedEnd && normalizedEnd < today ? normalizedEnd : today
          }
          yearItemNumber={yearItemNumber}
          enableTabLoop={false}
        />
        <DatePicker
          className={className}
          wrapperClassName={wrapperClassName}
          renderYearContent={renderYearContent}
          showYearPicker
          dateFormat="yyyy"
          showIcon={showIcon}
          inline={inline}
          toggleCalendarOnIconClick
          selected={normalizedEnd}
          onChange={(date) => onValueChange([normalizedStart, date])}
          placeholderText="End Year"
          maxDate={today}
          minDate={normalizedStart || undefined}
          yearItemNumber={yearItemNumber}
          enableTabLoop={false}
        />
      </DatePickerWrapper>
    );
  }
  if (mode === "in_quarter") {
    return (
      <DatePickerWrapper>
        <DatePicker
          className={className}
          wrapperClassName={wrapperClassName}
          renderQuarterContent={renderQuarterContent}
          showQuarterYearPicker
          dateFormat="QQQ, yyyy"
          showIcon={showIcon}
          inline={inline}
          toggleCalendarOnIconClick
          selected={normalizedValue}
          onChange={(date) => onValueChange([date])}
          maxDate={today}
          enableTabLoop={false}
        />
      </DatePickerWrapper>
    );
  }
  if (mode === "in_year") {
    return (
      <DatePickerWrapper>
        <DatePicker
          className={className}
          wrapperClassName={wrapperClassName}
          renderYearContent={renderYearContent}
          showYearPicker
          dateFormat="yyyy"
          showIcon={showIcon}
          inline={inline}
          toggleCalendarOnIconClick
          selected={normalizedValue}
          onChange={(date) => onValueChange([date])}
          maxDate={today}
          yearItemNumber={yearItemNumber}
          enableTabLoop={false}
        />
      </DatePickerWrapper>
    );
  }
  if (mode === "between_date") {
    return (
      <DatePickerWrapper>
        <DatePicker
          className={className}
          wrapperClassName={wrapperClassName}
          selectsRange
          startDate={normalizedStart}
          endDate={normalizedEnd}
          onChange={(dates) =>
            onValueChange(dates as [Date | null, Date | null])
          }
          dateFormat="dd-MM-yyyy"
          showIcon={showIcon}
          inline={inline}
          toggleCalendarOnIconClick
          maxDate={today}
          enableTabLoop={false}
        />
      </DatePickerWrapper>
    );
  }
  return (
    <DatePickerWrapper>
      <DatePicker
        className={className}
        wrapperClassName={wrapperClassName}
        selected={normalizedValue}
        onChange={(date) => onValueChange([date])}
        dateFormat="dd-MM-yyyy"
        showIcon={showIcon}
        inline={inline}
        toggleCalendarOnIconClick
        startDate={startDate ? (normalizedStart ?? undefined) : undefined}
        maxDate={startDate ?  undefined : today}
        enableTabLoop={false}
      />
    </DatePickerWrapper>
  );
};

export default CustomDatePicker;

const DatePickerWrapper = styled(Box)`
  .react-datepicker__year-wrapper,
  .react-datepicker__quarter-wrapper {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    max-height: 280px;
    overflow-y: auto;
    min-width: 170px;
    align-items: center;
  }
  [class$="text--disabled"], .react-datepicker__day--disabled {
    color: #b0b0b0 !important;
    opacity: 0.6;
    pointer-events: none !important;
    cursor: not-allowed !important;
  }
  .react-datepicker__triangle {
    display: none !important;
  }
  .react-datepicker {
    background-color: ${({ theme: { darkMode } }) =>
      darkMode ? Colors.DARK_GRAY3 : Colors.WHITE};

    border: none;
    color: inherit;
    box-shadow: ${({ theme: { darkMode } }) =>
      darkMode
        ? "0 0 0 1px #394b59, 0 4px 8px rgba(16,22,26,0.4)"
        : "0 0 0 1px #d8e1e8, 0 4px 8px rgba(16,22,26,0.15)"};
  }

  .react-datepicker__header {
    background-color: ${({ theme: { darkMode } }) =>
      darkMode ? Colors.DARK_GRAY3 : Colors.WHITE};
    border-color: ${({ theme: { darkMode } }) =>
      darkMode ? Colors.DARK_GRAY5 : Colors.LIGHT_GRAY3};
  }
  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name,
  .react-datepicker__current-month,
  .react-datepicker__header {
    color: inherit;
  }
  .react-datepicker__input-container svg {
    fill: #215db0 !important;
    color: #215db0 !important;
    width: 15px;
    height: 15px;
    margin-top: -1px;
    vertical-align: middle;
  }

  .react-datepicker__navigation {
    background: transparent !important;
    border-radius: 0 !important;
    width: 32px; /* Keep a reasonable clickable area */
    height: 32px;
    margin: 2px;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: none;
  }

  .react-datepicker__navigation-icon {
    top: 4px;
  }

  .react-datepicker__navigation:hover {
    background: transparent !important;
  }
  .react-datepicker__navigation-icon::before {
    border-color: #215db0 !important;
  }
  .react-datepicker__navigation--previous
    .react-datepicker__navigation-icon::before,
  .react-datepicker__navigation--next
    .react-datepicker__navigation-icon::before {
    border-color: #215db0 !important;
  }

  .react-datepicker__day:not([aria-disabled="true"]):hover,
  .react-datepicker__month-text:not([aria-disabled="true"]):hover,
  .react-datepicker__quarter-text:not([aria-disabled="true"]):hover,
  .react-datepicker__year-text:not([aria-disabled="true"]):hover {
    background: #e3eefc !important;
    color: #215db0 !important;
    border-radius: 6px;
  }

  .react-datepicker__navigation-icon::before {
    border-width: 2px 2px 0 0;
  }
  .react-datepicker__view-calendar-icon input {
    background-color: ${({ theme: { darkMode } }) =>
      darkMode ? Colors.DARK_GRAY2 : Colors.WHITE};
    border: 1px solid
      ${({ theme: { darkMode } }) => (darkMode ? Colors.GRAY1 : Colors.GRAY5)};
  }
`;